import { Subject } from '../_interfaces/subject';
import { Visit, visitLocation } from '../_interfaces/visit';
import { normalizeSubjectDobToDateOnly } from './dmy-date';
import { formatSubjectName } from './utils';

export type PatientInput = Subject | {
  subject_name?: string;
  subject_id?: string;
  subject_dob?: string;
  subject_visit_id?: string;
  subject_alternative_id?: string;
  subject_prefix?: string;
  subject_first_name?: string;
  subject_middle_name?: string;
  subject_last_name?: string;
  subject_sex?: string | null;
  subject_death_indicator?: string | null;
  facility_id?: number | null;
};

export interface FormatPatientOptions {
  manual?: boolean;
  visit?: Visit | null;
  /** Matched schedule visit used for Context display only — does not set visit_id. */
  scheduleVisit?: Visit | null;
  includeVisitFields: boolean;
  scheduleLocation?: string | null;
}

export interface VisitContextFields {
  visit_location: string | null;
  visit_hospital_service: string | null;
  visit_clinic_type_code: string | null;
  visit_admit_date_time: string | null;
  visit_discharge_date_time: string | null;
  visit_consultant_doctor: string | null;
  visit_consultant_doctor_prefix: string | null;
  visit_consultant_doctor_first_name: string | null;
  visit_consultant_doctor_last_name: string | null;
  visit_referring_doctor: string | null;
  visit_referring_doctor_prefix: string | null;
  visit_referring_doctor_first_name: string | null;
  visit_referring_doctor_last_name: string | null;
}

export interface PatientFields extends Partial<VisitContextFields> {
  subject_name: string;
  subject_prefix?: string | null;
  subject_first_name?: string | null;
  subject_middle_name?: string | null;
  subject_last_name?: string | null;
  subject_sex?: string | null;
  subject_death_indicator?: string | null;
  subject_id: string;
  subject_visit_id: string;
  subject_alternative_id: string;
  subject_dob: string | null;
  facility_id: number | null;
  order_id: number | null;
  visit_id?: number | null;
}

export const EMPTY_VISIT_CONTEXT: VisitContextFields = {
  visit_location: null,
  visit_hospital_service: null,
  visit_clinic_type_code: null,
  visit_admit_date_time: null,
  visit_discharge_date_time: null,
  visit_consultant_doctor: null,
  visit_consultant_doctor_prefix: null,
  visit_consultant_doctor_first_name: null,
  visit_consultant_doctor_last_name: null,
  visit_referring_doctor: null,
  visit_referring_doctor_prefix: null,
  visit_referring_doctor_first_name: null,
  visit_referring_doctor_last_name: null,
};

export function visitContextFields(
  visit?: Visit | null,
  scheduleLocation: string | null = null,
): VisitContextFields {
  if (!visit) {
    return { ...EMPTY_VISIT_CONTEXT, visit_location: scheduleLocation };
  }

  return {
    visit_location: visitLocation(visit) ?? scheduleLocation,
    visit_hospital_service: presentText(visit.visit_hospital_service),
    visit_clinic_type_code: presentText(visit.visit_clinic_type_code),
    visit_admit_date_time: presentText(visit.visit_admit_date_time),
    visit_discharge_date_time: presentText(visit.visit_discharge_date_time),
    visit_consultant_doctor: presentText(visit.visit_consultant_doctor),
    visit_consultant_doctor_prefix: presentText(visit.visit_consultant_doctor_prefix),
    visit_consultant_doctor_first_name: presentText(visit.visit_consultant_doctor_first_name),
    visit_consultant_doctor_last_name: presentText(visit.visit_consultant_doctor_last_name),
    visit_referring_doctor: presentText(visit.visit_referring_doctor),
    visit_referring_doctor_prefix: presentText(visit.visit_referring_doctor_prefix),
    visit_referring_doctor_first_name: presentText(visit.visit_referring_doctor_first_name),
    visit_referring_doctor_last_name: presentText(visit.visit_referring_doctor_last_name),
  };
}

export function formatPatient(
  patient: PatientInput,
  options: FormatPatientOptions
): PatientFields {
  const {
    manual,
    visit,
    scheduleVisit = null,
    includeVisitFields,
    scheduleLocation = null,
  } = options;

  const isSubject = 'subject_first_name' in patient || 'subject_last_name' in patient;
  const subjectName = isSubject ? formatSubjectName(patient as Subject) : (patient as any).subject_name || '';
  const dobValue = manual
    ? ((patient as any).subject_dob || null)
    : (normalizeSubjectDobToDateOnly((patient as any).subject_dob) ?? null);

  const base: PatientFields = {
    subject_name: subjectName,
    subject_prefix: presentText((patient as any).subject_prefix),
    subject_first_name: presentText((patient as any).subject_first_name),
    // Approval sends name parts and omits subjectName whenever any part is present.
    // Hoffweb then rebuilds the export name from those parts, so the middle name
    // cannot live only inside subject_name.
    subject_middle_name: presentText((patient as any).subject_middle_name),
    subject_last_name: presentText((patient as any).subject_last_name),
    subject_sex: presentText((patient as any).subject_sex),
    subject_death_indicator: presentText((patient as any).subject_death_indicator),
    subject_id: (patient as any).subject_id || '',
    // Downstream matches these against visit_number, which every reader trims, so they
    // are stored trimmed too. An explicitly chosen visit replaces the patient row's
    // value outright: falling back would point this at a different visit than visit_id.
    subject_visit_id: includeVisitFields && visit
      ? (visit.visit_number?.trim() || '')
      : ((patient as any).subject_visit_id?.trim() || ''),
    subject_alternative_id: (patient as any).subject_alternative_id?.trim() || '',
    subject_dob: dobValue,
    facility_id: (patient as any).facility_id ?? null,
    order_id: (patient as any).order_id ?? null,
  };

  if (!includeVisitFields) {
    return base;
  }

  const displayVisit = visit ?? scheduleVisit;
  return {
    ...base,
    visit_id: visit?.id ?? null,
    ...visitContextFields(displayVisit, scheduleLocation),
  };
}

function presentText(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed || null;
}

/** Copilot patient shell when starting without a selected patient. */
export function blankCopilotPatient(type: string): PatientFields & { type: string } {
  return {
    type,
    subject_name: '',
    subject_id: '',
    subject_visit_id: '',
    subject_alternative_id: '',
    subject_dob: null,
    facility_id: null,
    order_id: null,
    visit_id: null,
    ...EMPTY_VISIT_CONTEXT,
  };
}
